// ==========================================
// Hook Socket.IO
// ==========================================

import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../stores/authStore';
import { useGameStore } from '../stores/gameStore';
import { useOnlineStore } from '../stores/onlineStore';
import { useSocketStatusStore } from '../stores/socketStore';

// Utiliser Socket sans types stricts pour plus de flexibilité
type FlexibleSocket = Socket;

let socket: FlexibleSocket | null = null;

export function disconnectSocket() {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

export function useSocket() {
  const { token, isAuthenticated } = useAuthStore();
  const socketRef = useRef<FlexibleSocket | null>(null);
  
  // Récupérer les actions du game store
  const {
    setRoom,
    addPlayer,
    removePlayer,
    setPlayerReady,
    setHost,
    updateRoomSettings,
    startGame,
    setCurrentQuestion,
    showAnswer,
    updateScore,
    finishGame,
    leaveRoom,
    setCountdown,
  } = useGameStore();
  
  // Initialiser la connexion
  useEffect(() => {
    if (!isAuthenticated || !token) {
      if (socket) {
        socket.disconnect();
        socket = null;
      }
      return;
    }
    
    if (!socket) {
      socket = io(window.location.origin, {
        auth: { token },
        transports: ['polling'],
        upgrade: false,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });
      
      // Event listeners globaux
      socket.on('connect', () => {
        console.log('🔌 Connecté au serveur');
        useSocketStatusStore.getState().setStatus('connected');
      });

      socket.on('disconnect', (reason: string) => {
        console.log('🔌 Déconnecté:', reason);
        useSocketStatusStore.getState().setStatus('disconnected');
      });

      socket.on('connect_error', (error: Error) => {
        console.error('❌ Erreur de connexion:', error.message);
        useSocketStatusStore.getState().setStatus('disconnected');
      });

      socket.io.on('reconnect_attempt', () => {
        useSocketStatusStore.getState().setStatus('reconnecting');
      });
      
      // Stats en ligne
      socket.on('stats:online', (data: { count: number; activeGames?: number }) => {
        console.log('📊 Stats received:', data);
        useOnlineStore.getState().setOnlineCount(data.count);
        if (data.activeGames !== undefined) {
          useOnlineStore.getState().setActiveGamesCount(data.activeGames);
        }
      });
      
      // Room events
      socket.on('room:playerJoined', (data: { player: { odId: string; pseudo: string; avatar?: string } }) => {
        addPlayer({
          odId: data.player.odId,
          pseudo: data.player.pseudo,
          avatar: data.player.avatar,
          isHost: false,
          isReady: false,
        });
      });
      
      socket.on('room:playerLeft', (data: { odId: string }) => {
        removePlayer(data.odId);
      });
      
      socket.on('game:playerLeft', (data: { odId: string; pseudo: string; remainingPlayers: number }) => {
        console.log(`[GAME] Joueur ${data.pseudo} a quitté la partie. ${data.remainingPlayers} joueur(s) restant(s)`);
      });
      
      socket.on('room:playerReady', (data: { odId: string; isReady: boolean }) => {
        setPlayerReady(data.odId, data.isReady);
      });
      
      socket.on('room:hostChanged', (data: { newHostId: string }) => {
        setHost(data.newHostId);
      });
      
      socket.on('room:settingsUpdated', (settings: Record<string, unknown>) => {
        updateRoomSettings(settings);
      });
      
      socket.on('room:kicked', () => {
        leaveRoom();
        alert('Vous avez été expulsé de la salle');
      });
      
      socket.on('room:starting', (data: { gameId: number; countdown?: number }) => {
        // Déclencher le countdown chez TOUS les joueurs
        if (data.countdown) {
          setCountdown(data.countdown);
        }
        startGame(data.gameId);
      });
      
      socket.on('room:returnToLobby', () => {
        // Ne pas réinitialiser immédiatement - l'utilisateur doit voir l'écran de résultats
        // Le reset sera fait quand l'utilisateur retourne au lobby
        console.log('[SOCKET] room:returnToLobby reçu - l\'état sera reset au retour au lobby');
      });
      
      // Game events
      socket.on('game:start', (data: { gameId: number }) => {
        startGame(data.gameId);
      });

      // Receive initial power-ups from server
      socket.on('game:initialized', (data: { gameId: number; playerPowerUps?: Record<string, string[]> }) => {
        if (data.playerPowerUps) {
          const { user } = useAuthStore.getState();
          const myPowerUps = user?.odId ? data.playerPowerUps[user.odId] : undefined;
          if (myPowerUps) {
            useGameStore.setState({ availablePowerUps: myPowerUps as any[] });
          }
        }
      });
      
      socket.on('game:question', (question: any) => {
        // Utiliser le timestamp serveur pour la synchronisation
        const serverTime = question.serverTimestamp || Date.now();
        
        setCurrentQuestion({
          questionId: question.questionId,
          questionNumber: question.questionNumber || 1,
          totalQuestions: question.totalQuestions || 10,
          text: question.textFr || question.text,
          timeLimit: question.timeLimit || 30,
          type: question.type,
          category: question.category,
          difficulty: question.difficulty,
          imageUrl: question.imageUrl,
          audioUrl: question.audioUrl,
          videoUrl: question.videoUrl,
          options: question.options,
          timelineItems: question.timelineItems,
          cropX: question.cropX,
          cropY: question.cropY,
          zoomLevel: question.zoomLevel,
        }, serverTime);
      });
      
      socket.on('game:answer', (data: { correctAnswer: string; correctTimelineOrder?: any[]; playerResults: any[] }) => {
        showAnswer(data.correctAnswer, data.playerResults, data.correctTimelineOrder);
      });
      
      socket.on('game:finished', (data: { ranking: any[] }) => {
        finishGame(data.ranking);
      });

      // AI Fun Fact — dispatched as custom DOM event
      socket.on('game:funFact', (data: { fact: string }) => {
        window.dispatchEvent(new CustomEvent('game:funFact', { detail: data }));
      });

      // Power-up events — dispatched as custom DOM events for Game.tsx to catch
      socket.on('game:powerupImpact', (data: { type: string; fromPseudo: string; duration: number }) => {
        window.dispatchEvent(new CustomEvent('powerup:impact', { detail: data }));
      });
      socket.on('game:powerupQueued', (data: { fromPseudo: string; type: string; targetPseudo: string; fromOdId: string; targetOdId: string }) => {
        window.dispatchEvent(new CustomEvent('powerup:queued', { detail: data }));
      });
      socket.on('game:powerupUsed', (data: { player: string; type: string; targetPseudo?: string; isQueued?: boolean }) => {
        window.dispatchEvent(new CustomEvent('powerup:used', { detail: data }));
      });
      socket.on('game:shieldBlocked', (data: { type: string; fromPseudo: string }) => {
        window.dispatchEvent(new CustomEvent('powerup:shieldBlocked', { detail: data }));
      });
    }
    
    socketRef.current = socket;
    
    return () => {
      // Ne pas déconnecter lors du unmount, garder la connexion globale
    };
  }, [isAuthenticated, token]);
  
  // ========== Actions ==========
  
  const createRoom = useCallback((settings = {}) => {
    return new Promise<{ success: boolean; room?: any; error?: string }>((resolve) => {
      if (!socket) {
        resolve({ success: false, error: 'Non connecté' });
        return;
      }
      
      socket.emit('room:create', settings, (response: any) => {
        if (response.success && response.room) {
          setRoom(response.room);
        }
        resolve(response);
      });
    });
  }, [setRoom]);
  
  const joinRoom = useCallback((roomCode: string) => {
    return new Promise<{ success: boolean; room?: any; error?: string }>((resolve) => {
      if (!socket) {
        resolve({ success: false, error: 'Non connecté' });
        return;
      }
      
      socket.emit('room:join', roomCode, (response: any) => {
        if (response.success && response.room) {
          setRoom(response.room);
        }
        resolve(response);
      });
    });
  }, [setRoom]);
  
  const leaveRoomSocket = useCallback(() => {
    return new Promise<{ success: boolean }>((resolve) => {
      if (!socket) {
        resolve({ success: false });
        return;
      }
      
      socket.emit('room:leave', (response: any) => {
        // Always reset client state — even if server says we're not in a room
        leaveRoom();
        resolve(response);
      });
    });
  }, [leaveRoom]);
  
  const setReady = useCallback((isReady: boolean) => {
    return new Promise<{ success: boolean }>((resolve) => {
      if (!socket) {
        resolve({ success: false });
        return;
      }
      
      socket.emit('room:ready', isReady, (response: any) => resolve(response));
    });
  }, []);
  
  const updateSettings = useCallback((settings: any) => {
    return new Promise<{ success: boolean; settings?: any }>((resolve) => {
      if (!socket) {
        resolve({ success: false });
        return;
      }
      
      socket.emit('room:settings', settings, (response: any) => resolve(response));
    });
  }, []);
  
  const kickPlayer = useCallback((odId: string) => {
    return new Promise<{ success: boolean }>((resolve) => {
      if (!socket) {
        resolve({ success: false });
        return;
      }
      
      socket.emit('room:kick', odId, (response: any) => resolve(response));
    });
  }, []);
  
  const startGameSocket = useCallback(() => {
    return new Promise<{ success: boolean; gameId?: number; error?: string }>((resolve) => {
      if (!socket) {
        resolve({ success: false, error: 'Non connecté' });
        return;
      }
      
      socket.emit('room:start', (response: any) => resolve(response));
    });
  }, []);
  
  const submitAnswer = useCallback((gameId: number, answer: string) => {
    return new Promise<{
      success: boolean;
      correct?: boolean;
      points?: number;
      newScore?: number;
      streak?: number;
      error?: string;
    }>((resolve) => {
      if (!socket) {
        resolve({ success: false, error: 'Non connecté' });
        return;
      }
      
      const { submitAnswer: markAnswered } = useGameStore.getState();
      markAnswered();
      
      socket.emit('game:answer', { gameId, answer }, (response: any) => {
        if (response.success && response.newScore !== undefined) {
          updateScore(response.newScore, response.streak || 0);
          // Streak reward: bonus power-up
          if (response.bonusPowerUp) {
            const { availablePowerUps } = useGameStore.getState();
            useGameStore.setState({ availablePowerUps: [...availablePowerUps, response.bonusPowerUp] });
            window.dispatchEvent(new CustomEvent('powerup:bonus', { detail: { type: response.bonusPowerUp } }));
          }
        }
        resolve(response);
      });
    });
  }, [updateScore]);
  
  const usePowerUp = useCallback((gameId: number, type: string, targetOdId?: string) => {
    return new Promise<{ success: boolean; hint?: any; error?: string }>((resolve) => {
      if (!socket) {
        resolve({ success: false, error: 'Non connecté' });
        return;
      }
      
      const { usePowerUp: removePowerUp } = useGameStore.getState();
      
      socket.emit('game:powerup', { gameId, type, targetOdId }, (response: any) => {
        if (response.success) {
          removePowerUp(type as any);
        }
        resolve(response);
      });
    });
  }, []);
  
  const sendChatMessage = useCallback((message: string) => {
    return new Promise<{ success: boolean }>((resolve) => {
      if (!socket) {
        resolve({ success: false });
        return;
      }
      
      socket.emit('chat:message', { message }, (response: any) => resolve(response));
    });
  }, []);
  
  const sendReaction = useCallback((emoji: string) => {
    return new Promise<{ success: boolean }>((resolve) => {
      if (!socket) {
        resolve({ success: false });
        return;
      }
      
      socket.emit('chat:reaction', { emoji }, (response: any) => resolve(response));
    });
  }, []);
  
  const sendTyping = useCallback((isTyping: boolean) => {
    if (!socket) return;
    socket.emit('chat:typing', isTyping);
  }, []);
  
  const listRooms = useCallback(() => {
    return new Promise<{ success: boolean; rooms?: any[]; error?: string }>((resolve) => {
      if (!socket) {
        resolve({ success: false, error: 'Non connecté' });
        return;
      }
      
      socket.emit('room:list', (response: any) => resolve(response));
    });
  }, []);
  
  const joinRoomWithPassword = useCallback((roomCode: string, password: string) => {
    return new Promise<{ success: boolean; room?: any; error?: string }>((resolve) => {
      if (!socket) {
        resolve({ success: false, error: 'Non connecté' });
        return;
      }
      
      socket.emit('room:joinWithPassword', { roomCode, password }, (response: any) => {
        if (response.success && response.room) {
          setRoom(response.room);
        }
        resolve(response);
      });
    });
  }, [setRoom]);
  
  return {
    socket: socket,
    isConnected: socket?.connected ?? false,
    
    // Room actions
    createRoom,
    joinRoom,
    joinRoomWithPassword,
    listRooms,
    leaveRoom: leaveRoomSocket,
    setReady,
    updateSettings,
    kickPlayer,
    startGame: startGameSocket,
    
    // Game actions
    submitAnswer,
    usePowerUp,
    
    // Chat
    sendChatMessage,
    sendReaction,
    sendTyping,
  };
}
